import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Avatar,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  AccessTime,
  CalendarMonth,
  Download,
  TrendingUp,
} from "@mui/icons-material";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import { useStudentAttendance, useAttendanceStatistics } from "../../hooks/queries/useAttendance";
import { useSelector } from "react-redux";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const Attendance = () => {
  const { user } = useSelector((state) => state.auth);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const { data: attendanceResponse, isLoading: attendanceLoading } = useStudentAttendance(
    user?.id,
    { month: selectedMonth + 1, year: selectedYear }
  );
  const { data: statistics, isLoading: statsLoading } = useAttendanceStatistics({
    month: selectedMonth + 1,
    year: selectedYear,
  });
  
  const attendance = attendanceResponse?.attendance || attendanceResponse || [];
  const loading = attendanceLoading || statsLoading;

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Sample data for demo
  const sampleStats = {
    totalDays: 22,
    present: 18,
    absent: 2,
    leave: 2,
    percentage: 81.8,
  };

  const sampleAttendance = [
    { date: "2025-12-01", status: "present", subject: "Mathematics" },
    { date: "2025-12-02", status: "present", subject: "Physics" },
    { date: "2025-12-03", status: "present", subject: "Chemistry" },
    { date: "2025-12-04", status: "absent", subject: "English" },
    { date: "2025-12-05", status: "present", subject: "Biology" },
    { date: "2025-12-08", status: "present", subject: "Mathematics" },
    { date: "2025-12-09", status: "leave", subject: "Physics" },
    { date: "2025-12-10", status: "present", subject: "Chemistry" },
    { date: "2025-12-11", status: "present", subject: "English" },
    { date: "2025-12-12", status: "present", subject: "Biology" },
    { date: "2025-12-15", status: "present", subject: "Mathematics" },
    { date: "2025-12-16", status: "present", subject: "Physics" },
    { date: "2025-12-17", status: "present", subject: "Chemistry" },
    { date: "2025-12-18", status: "present", subject: "English" },
  ];

  const displayStats = statistics || sampleStats;
  const displayAttendance =
    attendance.length > 0 ? attendance : sampleAttendance;

  const attendanceData = {
    labels: ["Present", "Absent", "Leave"],
    datasets: [
      {
        data: [displayStats.present, displayStats.absent, displayStats.leave],
        backgroundColor: ["#4caf50", "#f44336", "#ff9800"],
        borderWidth: 0,
      },
    ],
  };

  const monthlyData = {
    labels: months.slice(0, selectedMonth + 1),
    datasets: [
      {
        label: "Attendance %",
        data: [85, 90, 88, 92, 87, 91, 89, 93, 85, 88, 90, 82].slice(
          0,
          selectedMonth + 1
        ),
        backgroundColor: "#1976d2",
      },
    ],
  };

  const getStatusChip = (status) => {
    const normalized = String(status || "").toLowerCase();
    const display = normalized === "on_leave" ? "present" : normalized;
    switch (display) {
      case "present":
        return (
          <Chip
            icon={<CheckCircle />}
            label="Present"
            size="small"
            color="success"
          />
        );
      case "absent":
        return (
          <Chip icon={<Cancel />} label="Absent" size="small" color="error" />
        );
      case "leave":
        return (
          <Chip
            icon={<AccessTime />}
            label="Leave"
            size="small"
            color="warning"
          />
        );
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            My Attendance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View your attendance records and statistics
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Download />}>
          Download Report
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {months.map((month, index) => (
                  <MenuItem key={month} value={index}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {[2023, 2024, 2025].map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: "primary.main", color: "white" }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h3" fontWeight={700}>
                {displayStats.percentage}%
              </Typography>
              <Typography variant="body2">Attendance Rate</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar sx={{ bgcolor: "success.light", mx: "auto", mb: 1 }}>
                <CheckCircle color="success" />
              </Avatar>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {displayStats.present}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Present Days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar sx={{ bgcolor: "error.light", mx: "auto", mb: 1 }}>
                <Cancel color="error" />
              </Avatar>
              <Typography variant="h4" fontWeight={700} color="error.main">
                {displayStats.absent}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Absent Days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar sx={{ bgcolor: "warning.light", mx: "auto", mb: 1 }}>
                <AccessTime color="warning" />
              </Avatar>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {displayStats.leave}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Leave Days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Attendance Distribution
              </Typography>
              <Box
                sx={{
                  height: 250,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Doughnut
                  data={attendanceData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: "bottom" } },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Monthly Trend
              </Typography>
              <Box sx={{ height: 250 }}>
                <Bar
                  data={monthlyData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      y: { beginAtZero: true, max: 100 },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Attendance Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Attendance Records - {months[selectedMonth]} {selectedYear}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Day</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayAttendance.map((record, index) => {
                  const date = new Date(record.date);
                  const dayName = date.toLocaleDateString("en-US", {
                    weekday: "long",
                  });
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CalendarMonth
                            sx={{ fontSize: 18, color: "text.secondary" }}
                          />
                          {record.date}
                        </Box>
                      </TableCell>
                      <TableCell>{dayName}</TableCell>
                      <TableCell>{record.subject}</TableCell>
                      <TableCell>{getStatusChip(record.status)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Attendance;
